import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Button,
  FormControl,
  FormLabel,
  Alert,
  CircularProgress,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { uwinAPI } from "../../../services/uwin.api";
import { decryptOpenSSLAES } from "./uwin-decrypt";
import { encryptText, getUwinClientToken } from "../../../helpers/common-uwinhelper.js";
import "./uwin.css";

const PersonalDetailsuwin = ({ beneficiaryData, identificationData, onContinue, txnID, onResendOTP}) => {

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSkipOtpConsent, setShowSkipOtpConsent] = useState(false); // State to show/hide consent checkbox
  const [resendTimer, setResendTimer] = useState(0); // Timer for resend button
  const [beneficiaryProfileState, setBeneficiaryProfileState] = useState(null); // Saved infant profile data

  // Check beneficiary type to determine which fields to show
  const beneficiaryType = beneficiaryData?.current_beneficiary_type_name || "";
  const isPregnantWoman = beneficiaryType === "Pregnant Woman";
  const isAdolescent = beneficiaryType === "Adolescent";
  const showDeliveryQuestions =
    beneficiaryData?.beneficiary_gender === "Female" &&
    beneficiaryData?.age >= 12 &&
    beneficiaryData?.age <= 49 &&
    !isPregnantWoman; // Show delivery questions for non-pregnant females aged 12–49

  const maskNumber = (num) => {
  const str = String(num);
    return str.slice(0, -4).replace(/./g, "X") + str.slice(-4);
  };

  // Extract beneficiary_id (decrypt if needed)
  const getBeneficiaryId = () => {
    if (beneficiaryData?.beneficiary_id) {
      try {
        return decryptOpenSSLAES(beneficiaryData.beneficiary_id);
      } catch (error) {
        console.error("Error decrypting beneficiary_id:", error);
        return beneficiaryData.beneficiary_id;
      }
    }
    return null;
  };

  // Helper function to convert file to base64 (moved from MemberDrawer)
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove the data URL prefix (e.g., "data:image/png;base64,")
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  // Authentication type options
  const authenticationTypes = [
    { value: "year_of_birth", label: "Year Of Birth" },
    { value: "beneficary_reference_id", label: "Last 4 digits of Beneficary ReferenceID" },
    { value: "multifactorial_password", label: "Multifactorial Password" },
  ];

  // Shared helper functions to define "filled" state consistently across all hemoglobin fields
const isHbValueFilled = (val) =>
  val !== "" && val !== null && val !== undefined && !isNaN(parseFloat(val));

const isHbDateFilled = (val) => {
  if (val instanceof Date) return !isNaN(val.getTime());
  if (typeof val === "string") return val.trim() !== "";
  return false;
};

const isCounsellingFilled = (val) =>
  typeof val === "string" && val.trim() !== "";

// Form validation schema with Yup
const validationSchema = Yup.object({
  is_pvtg: Yup.string()
    .required("PVTG selection is required"),

  hb_testing_date: Yup.date()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === "" ? null : value
    )
    .min(dayjs().subtract(1, 'month').toDate(), "Date cannot be older than one month")
    .max(new Date(), "Date cannot be in the future")
    .test(
      "hb-date-not-before-dob",
      "Date of Hb Testing cannot be before the date of birth",
      function (value) {
        if (!value) return true; // Let other validators handle empty
        const rawDob = beneficiaryData?.dob;
        if (!rawDob) return true;
        // DOB from API is "DD-Mon-YYYY" (e.g. "01-Jan-1996")
        const dob = dayjs(rawDob, "DD-MMM-YYYY");
        if (!dob.isValid()) return true;
        return !dayjs(value).isBefore(dob, 'day');
      }
    )
    .test(
      "hb-group-date-required",
      "Date of Hb Testing is required ",
      function (value) {
        const { hb_value, counselling_done } = this.parent;
        // Only enforce this field if at least one SIBLING is filled
        const anyOtherFilled = isHbValueFilled(hb_value) || isCounsellingFilled(counselling_done);
        if (!anyOtherFilled) return true;
        return isHbDateFilled(value);
      }
    ),

  hb_value: Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      isNaN(originalValue) || originalValue === "" ? null : value
    )
    .min(1, "Value must be between 1 to 20")
    .max(20, "Value must be between 1 to 20")
    .test(
      "hb-group-value-required",
      "Hb Value is required ",
      function (value) {
        const { hb_testing_date, counselling_done } = this.parent;
        const anyOtherFilled = isHbDateFilled(hb_testing_date) || isCounsellingFilled(counselling_done);
        if (!anyOtherFilled) return true;
        return isHbValueFilled(value);
      }
    ),

  counselling_done: Yup.string()
    .nullable()
    .test(
      "hb-group-counselling-required",
      "Counselling selection is required ",
      function (value) {
        const { hb_value, hb_testing_date } = this.parent;
        const anyOtherFilled = isHbValueFilled(hb_value) || isHbDateFilled(hb_testing_date);
        if (!anyOtherFilled) return true;
        return isCounsellingFilled(value);
      }
    ),

    // Conditional OTP validation - required only if skip OTP consent is not checked
    otp: Yup.string()
      .when("skipOtpConsent", {
        is: false,
        then: (schema) => schema.required("OTP is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

    // Skip OTP fields
    skipOtpConsent: Yup.boolean(),

    // Authentication type - required if skip OTP consent is checked
    verification_method: Yup.string()
      .when("skipOtpConsent", {
        is: true,
        then: (schema) => schema.required("Authentication type is required"),
        otherwise: (schema) => schema.notRequired(),
      }),

  // Conditional fields for non-pregnant females aged 12–49
  ...(showDeliveryQuestions && {
    is_delivery_in_last_six_months: Yup.string()
      .nullable(),

    is_child_alive: Yup.string()
      .nullable()
      .when("is_delivery_in_last_six_months", {
        is: "Yes",
        then: (schema) => schema.required("This field is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
  }),

  // Authentication value - required if skip OTP consent is checked
  verification_value: Yup.string()
  .when("skipOtpConsent", {
    is: true,
    then: (schema) => schema.required("Authentication value is required")
      .when("verification_method", {
        is: "year_of_birth",
        then: (schema) => schema.matches(/^\d{4}$/, "Year must be 4 digits"),
        // ADD THIS NEW VALIDATION FOR MULTIFACTORIAL PASSWORD
        otherwise: (schema) => schema.when("verification_method", {
          is: "multifactorial_password",
          then: (schema) => schema.min(8, "Must be at least 8 characters (4 letters + 4 digits)"),
          otherwise: (schema) => schema.min(4, "Minimum 4 characters required"),
        }),
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
  });

  // Initial form values
  const initialValues = {
    is_pvtg: beneficiaryData?.is_pvtg || "",
    hb_testing_date: "",
    hb_value: "",
    counselling_done: "",
    is_delivery_in_last_six_months: "",
    is_child_alive: "",
    otp: "",
    skipOtpConsent: false,
    verification_method: "",
    verification_value: "",
    father_name: beneficiaryProfileState?.father_name || "",
    child_weight: beneficiaryProfileState?.child_weight || "",
  };

  // Handle resend OTP with timer
  const handleResendOTP = async () => {
    if (onResendOTP) {
      const success = await onResendOTP();
      if (success) {
        setResendTimer(180);

        const timer = setInterval(() => {
          setResendTimer((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } else {
      toast.info("OTP resent successfully");
    setResendTimer(180);

    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }
  };

  // Add this useEffect after your existing useEffect
  // eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  // Start initial 180-second timer when component mounts
  setResendTimer(180);

  const timer = setInterval(() => {
    setResendTimer((prev) => {
      if (prev <= 1) {
        clearInterval(timer);
        return 0;
      }
      return prev - 1;
    });
  }, 1000);

  // Cleanup timer on unmount
  return () => clearInterval(timer);
}, []); // Empty dependency array is correct here - we only want this to run on mount

  // Handle skip OTP click
  const handleSkipOtpClick = (setFieldValue) => {
    setShowSkipOtpConsent(true);
    // Clear OTP field when skip is clicked
    setFieldValue("otp", "");
  };

   const encryptText = async (plaintext) => {
          try {
              const payload = {
                  plaintext: plaintext
              }
              // Fetch the parent profile data
              const resEncrypt = await uwinAPI.encryptText(payload);
              return resEncrypt.data.encrypted;
          } catch (error) {
              console.error("Backend login error:", error);
          }
      };

    function getVerifierDevice() {
    const ua = navigator.userAgent;

    // Windows
    const winMatch = ua.match(/Windows NT [0-9.]+/);
    if (winMatch) return winMatch[0];

    // Android
    const androidMatch = ua.match(/Android [0-9.]+/);
    if (androidMatch) return androidMatch[0];

    // iPhone/iPad
    if (/iPhone/.test(ua)) return "iPhone";
    if (/iPad/.test(ua)) return "iPad";

    // Mac
    if (/Mac OS X/.test(ua)) return "Mac OS X";

    return "Unknown Device";
  }

  useEffect(()=>{
    const getBeneficiaryProfileData = async () =>{
          if(beneficiaryData?.current_beneficiary_type_name == "Infant" || 
beneficiaryData?.current_beneficiary_type_name == "New Born Baby"
){
            const beneficiaryProfile = await uwinAPI.getBeneficiaryProfile({ beneficiary_id: beneficiaryData.beneficiary_id });
            console.log("beneficiaryProfile",beneficiaryProfile?.data)
            console.log(beneficiaryProfile?.data?.child_weight) 
            console.log(beneficiaryProfile?.data?.father_name) 
            setBeneficiaryProfileState(beneficiaryProfile?.data || null);
          }
        }
        getBeneficiaryProfileData()
  },[beneficiaryData?.current_beneficiary_type_name, beneficiaryData?.beneficiary_id])

// Handle form submission (UPDATED with identification API call)
const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
  console.log("Form values:", values);
  try {
    setIsSubmitting(true);

    const beneficiaryId = getBeneficiaryId();
    if (!beneficiaryId) {
      toast.error("Beneficiary ID not found");
      return;
    }

    // STEP 1: Handle OTP verification (Main Gatekeeper)
    // Check if txnID exists before proceeding with OTP verification
    if (!values.skipOtpConsent && !txnID) {
      toast.error("Session expired. Please close this panel and restart the verification process for the beneficiary.");
      return;
    }

    const OTPpayload = {};
      
    if (values.skipOtpConsent) {
      // Skip OTP - use manual verification
      OTPpayload.verification_method = values?.verification_method;
      OTPpayload.verification_value = await encryptText(values?.verification_value);
      OTPpayload.beneficiary_id = decryptOpenSSLAES(beneficiaryData?.beneficiary_id);
      OTPpayload.facility_id = beneficiaryData?.facility_id;
      OTPpayload.session_site_id = beneficiaryData?.session_site_id;
      OTPpayload.session_id = decryptOpenSSLAES(beneficiaryData?.session_id);
      OTPpayload.verifier_device = getVerifierDevice();
    } else {
      // Use OTP verification
      OTPpayload.otp = await encryptText(values.otp);
      OTPpayload.txnId = await encryptText(txnID);
    }

    console.log("Submitting verification payload:", { OTPpayload });

    const extractErrorMessage = (errorObj, defaultMsg = "Verification failed") => {
      if (!errorObj) return defaultMsg;
      if (typeof errorObj === 'string') return errorObj;
      if (typeof errorObj === 'object') {
        return errorObj.error || errorObj.message || JSON.stringify(errorObj);
      }
      return defaultMsg;
    };

    let otpSuccess = false;
    let otpErrorMessage = null;

    try {
      const OTPresponse = values.skipOtpConsent ? await uwinAPI.verifyBeneficiary(OTPpayload) : await uwinAPI.userVerifyOTP(OTPpayload);

      if (OTPresponse?.success || OTPresponse?.status) {
        otpSuccess = true;
        console.log("Identity verification successful");
      } else {
        otpErrorMessage = extractErrorMessage(OTPresponse?.message, "Verification failed");
      }
    } catch (otpError) {
      console.error("OTP verification error:", otpError);
      const resData = otpError?.response?.data;
      otpErrorMessage = extractErrorMessage(resData?.message || resData?.error, "Verification failed");
    }

    // STEP 2: Only proceed with data updates if verification was successful
    if (otpSuccess) {
      // 2A: Handle Beneficiary Metadata Update API
      try {
        const token = await getUwinClientToken();
        if (token) {
          const formData = new FormData();
          formData.append("is_pvtg", beneficiaryData?.is_pvtg || "");
          formData.append("beneficiary_id", beneficiaryId);
          // Always send both keys so the backend multipart contract is satisfied.
          // When both are already stored in the backend (hasBothPhotoIdFields),
          // send empty strings so the backend retains the existing real data
          // (the number we receive is masked and must not be sent back).
          // When the user manually entered them, send the entered values.
          formData.append("photo_id_type_id", identificationData?.hasBothPhotoIdFields ? "" : identificationData?.selectedPhotoIdType || "");
          formData.append("photo_id_number",   identificationData?.hasBothPhotoIdFields ? "" : identificationData?.photoIdNumber || "");

          if (identificationData?.selectedFile) {
            formData.append("photo_id_file", identificationData.selectedFile);
          }

          const isInfantOrNewBorn = beneficiaryData?.current_beneficiary_type_name === "Infant" || beneficiaryData?.current_beneficiary_type_name === "New Born Baby";
          formData.append("father_name", isInfantOrNewBorn ? (values.father_name || "") : "");
          formData.append("child_weight", isInfantOrNewBorn ? (values.child_weight ? values.child_weight.toString() : "") : "");

          console.log("🔒 Updating Beneficiary Metadata (Binary Upload)");
          const verificationResponse = await uwinAPI.updateBeneficiaryVerify(formData);

          if (!verificationResponse?.success) {
            toast.error(verificationResponse?.message || "Failed to update beneficiary metadata");
          }
        }
      } catch (verificationError) {
        console.error("Verification update error:", verificationError);
        toast.error(verificationError?.response?.data?.message || "Error while updating metadata");
      }

      // 2B: Handle Hemoglobin API (using existing conditional logic)
      const hbValueFilled = values.hb_value !== "" && values.hb_value !== null && !isNaN(parseFloat(values.hb_value));
      const hbDateFilled = typeof values.hb_testing_date === "string" && values.hb_testing_date.trim() !== "";
      const counsellingFilled = typeof values.counselling_done === "string" && values.counselling_done.trim() !== "";

      if (hbValueFilled || hbDateFilled || counsellingFilled) {
        const payload = {
          beneficiary_id: beneficiaryId,
          hb_value: parseFloat(values.hb_value),
          hb_testing_date: dayjs(values.hb_testing_date).format("YYYY-MM-DD"),
          counselling_done: values.counselling_done,
        };

        if (showDeliveryQuestions && values.is_delivery_in_last_six_months === "Yes" && values.is_child_alive) {
          payload.is_delivery_in_last_six_months = values.is_delivery_in_last_six_months;
          payload.is_child_alive = values.is_child_alive;
        } else if (showDeliveryQuestions && values.is_delivery_in_last_six_months) {
          payload.is_delivery_in_last_six_months = values.is_delivery_in_last_six_months;
        }

        try {
          const response = await uwinAPI.saveHemoglobinDetail(payload);
          if (!response?.success) {
            toast.error(response?.message || "Failed to save hemoglobin details");
          }
        } catch (hemoglobinError) {
          console.error("Hemoglobin save error:", hemoglobinError);
          toast.error(hemoglobinError?.response?.data?.message || "Failed to save hemoglobin details");
        }
      }

      // 2C: Final Success Actions
      toast.success("Verification successful");
      if (onContinue) {
        onContinue();
      }
    } else {
      // OTP/Verification failed - show error and stay on page
      toast.error(otpErrorMessage || "Verification failed");
    }

  } catch (error) {
    console.error("Unexpected error in form submission:", error);
    toast.error("An unexpected error occurred. Please try again.");
  } finally {
    setIsSubmitting(false);
    setSubmitting(false);
  }
};

  return (
    <Box>
      <Box>
        {/* ID Type */}
        <Typography variant="body2" fontWeight={500}>
          {(() => {
            try {
              return beneficiaryData?.photo_id_type 
                ? decryptOpenSSLAES(beneficiaryData.photo_id_type)
                : "NA";
            } catch (error) {
              console.error("Error decrypting photo_id_type:", error);
              return "NA";
            }
          })()}
        </Typography>

        {/* ID Number */}
        <Typography sx={{ mb: 2 }}>
          {(() => {
            try {
              return beneficiaryData?.photo_id_number 
                ? decryptOpenSSLAES(beneficiaryData.photo_id_number)
                : "NA";
            } catch (error) {
              console.error("Error decrypting photo_id_number:", error);
              return "NA";
            }
          })()}
        </Typography>
      </Box>
    
      {/* Name */}
      <Typography variant="body2" fontWeight={500}>
        Name
      </Typography>
      <Typography sx={{ mb: 2 }}>{beneficiaryData?.ben_name ?? "NA"}</Typography>

      {/* Gender */}
      <Typography variant="body2" fontWeight={500}>
        Gender
      </Typography>
      <Typography sx={{ mb: 2 }}>{beneficiaryData?.beneficiary_gender ?? "NA"}</Typography>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue, setFieldTouched, isSubmitting: formSubmitting }) => (
          <Form>
            {/* Infant / New Born Baby Fields */}
            {(beneficiaryData?.current_beneficiary_type_name === "Infant" || beneficiaryData?.current_beneficiary_type_name === "New Born Baby") && (
              <>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Child Weight at Birth In KG's
                  </Typography>
                  {beneficiaryProfileState?.child_weight ? (
                    <Typography sx={{ mb: 2 }}>{beneficiaryProfileState.child_weight}</Typography>
                  ) : (
                    <Field name="child_weight">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Child Weight at Birth In KG's"
                          type="number"
                          inputProps={{ min: 0, step: 0.1 }}
                          variant="standard"
                          sx={{ mt: 1 }}
                          error={!!(meta.error && meta.touched)}
                          helperText={meta.error && meta.touched ? meta.error : ""}
                        />
                      )}
                    </Field>
                  )}
                </Box>
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Father Name
                  </Typography>
                  {beneficiaryProfileState?.father_name ? (
                    <Typography sx={{ mb: 2 }}>{beneficiaryProfileState.father_name}</Typography>
                  ) : (
                    <Field name="father_name">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder="Enter Father's Name"
                          variant="standard"
                          sx={{ mt: 1 }}
                          error={!!(meta.error && meta.touched)}
                          helperText={meta.error && meta.touched ? meta.error : ""}
                        />
                      )}
                    </Field>
                  )}
                </Box>
              </>
            )}

            {/* PVTG */}
            <FormControl component="fieldset" sx={{ mt: 2, width: "100%" }} error={!!(errors.is_pvtg && touched.is_pvtg)}>
              <FormLabel component="legend" sx={{ color: "text.primary", fontWeight: 500 }}>
                PVTG <span style={{ color: "red", fontSize: "20px" }}>*</span>
              </FormLabel>
              <Field name="is_pvtg">
                {({ field }) => (
                  <RadioGroup
                    {...field}
                    row
                    value={values.is_pvtg}
                    onChange={(e) => setFieldValue("is_pvtg", e.target.value)}
                  >
                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="No" control={<Radio />} label="No" />
                  </RadioGroup>
                )}
              </Field>
              {errors.is_pvtg && touched.is_pvtg && (
                <Typography variant="caption" color="error">
                  {errors.is_pvtg}
                </Typography>
              )}
            </FormControl>

            {/* Date of Hb Testing */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight={500}>
                Date Of Hb Testing
                {/* Show required indicator if any other hb field is filled */}
                {(values.hb_value || values.counselling_done) && (
                  <span style={{ color: "red", fontSize: "20px" }}> *</span>
                )}
              </Typography>
              <Field name="hb_testing_date">
                {({ field, meta }) => {
                  // DOB from API is "DD-Mon-YYYY" (e.g. "01-Jan-1996").
                  // The date picker min is the LATER of (DOB, one-month-ago)
                  // so the browser also rejects dates before birth.
                  const dobMin = beneficiaryData?.dob
                    ? dayjs(beneficiaryData.dob, "DD-MMM-YYYY")
                    : null;
                  const oneMonthAgo = dayjs().subtract(1, 'month');
                  const effectiveMin = dobMin?.isValid() && dobMin.isAfter(oneMonthAgo)
                    ? dobMin
                    : oneMonthAgo;
                  return (
                    <TextField
                      {...field}
                      fullWidth
                      type="date"
                      sx={{ mt: 1 }}
                      className="pd-input"
                      inputProps={{
                        min: effectiveMin.format("YYYY-MM-DD"),
                        max: dayjs().format("YYYY-MM-DD"),
                      }}
                      error={!!(meta.error && meta.touched)}
                      helperText={meta.error && meta.touched ? meta.error : ""}
                    />
                  );
                }}
              </Field>
            </Box>

            {/* Hb Value */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight={500}>
                Hb Value
                {/* Show required indicator if any other hb field is filled */}
                {(values.hb_testing_date || values.counselling_done) && (
                  <span style={{ color: "red", fontSize: "20px" }}> *</span>
                )}
              </Typography>
              <Field name="hb_value">
                {({ field, meta }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="g/dL"
                    type="number"
                    inputProps={{
                      min: 1,
                      max: 20,
                      step: 0.1,
                    }}
                    className="pd-input"
                    error={!!(meta.error && meta.touched)}
                    helperText={meta.error && meta.touched ? meta.error : ""}
                  />
                )}
              </Field>
            </Box>

            {/* Delivery questions - Only for non-pregnant females aged 12–49 */}
            {showDeliveryQuestions && (
              <>
                {/* Delivered in last 6 months */}
                <FormControl 
                  component="fieldset" 
                  sx={{ mt: 2, width: "100%" }} 
                  error={!!(errors.is_delivery_in_last_six_months && touched.is_delivery_in_last_six_months)}
                >
                  <FormLabel component="legend" sx={{ color: "text.primary", fontWeight: 500 }}>
                    Delivered in last six months?
                  </FormLabel>
                  <Field name="is_delivery_in_last_six_months">
                    {({ field }) => (
                      <RadioGroup
                        {...field}
                        row
                        value={values.is_delivery_in_last_six_months}
                        onChange={(e) => {
                          setFieldValue("is_delivery_in_last_six_months", e.target.value);
                          // Reset child alive field and its touched state if No is selected
                          if (e.target.value === "No") {
                            setFieldValue("is_child_alive", "");
                            setFieldTouched("is_child_alive", false); // clears stale error immediately
                          }
                        }}
                      >
                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                    )}
                  </Field>
                  {errors.is_delivery_in_last_six_months && touched.is_delivery_in_last_six_months && (
                    <Typography variant="caption" color="error">
                      {errors.is_delivery_in_last_six_months}
                    </Typography>
                  )}
                </FormControl>

                  {/* Child alive - Always visible; disabled when delivery is "No", required when "Yes" */}
                  <FormControl
                    component="fieldset"
                    sx={{ mt: 2, width: "100%" }}
                    error={!!(errors.is_child_alive && touched.is_child_alive)}
                  >
                    <FormLabel component="legend" sx={{ color: "text.primary", fontWeight: 500 }}>
                      Child is alive?
                      {values.is_delivery_in_last_six_months === "Yes" && (
                        <span style={{ color: "red", fontSize: "20px" }}> *</span>
                      )}
                    </FormLabel>
                    <Field name="is_child_alive">
                      {({ field }) => (
                        <RadioGroup
                          {...field}
                          row
                          value={values.is_child_alive}
                          onChange={(e) => setFieldValue("is_child_alive", e.target.value)}
                        >
                          <FormControlLabel
                            value="Yes"
                            control={<Radio />}
                            label="Yes"
                            disabled={values.is_delivery_in_last_six_months === "No"}
                          />
                          <FormControlLabel
                            value="No"
                            control={<Radio />}
                            label="No"
                            disabled={values.is_delivery_in_last_six_months === "No"}
                          />
                        </RadioGroup>
                      )}
                    </Field>
                    {errors.is_child_alive && touched.is_child_alive && (
                      <Typography variant="caption" color="error">
                        {errors.is_child_alive}
                      </Typography>
                    )}
                  </FormControl>
                
              </>
            )}

            {/* Counselling */}
            <FormControl
              component="fieldset"
              sx={{ mt: 2, width: "100%" }}
              error={!!(errors.counselling_done && touched.counselling_done)}
            >
              <FormLabel component="legend" sx={{ color: "text.primary", fontWeight: 500 }}>
                Whether Counselling has been done?
                {/* Show required indicator if any hb field is filled */}
                {(values.hb_value || values.hb_testing_date) && (
                  <span style={{ color: "red", fontSize: "20px" }}> *</span>
                )}
              </FormLabel>
              <Field name="counselling_done">
                {({ field }) => (
                  <RadioGroup
                    {...field}
                    row
                    value={values.counselling_done}
                    onChange={(e) => setFieldValue("counselling_done", e.target.value)}
                  >
                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="No" control={<Radio />} label="No" />
                  </RadioGroup>
                )}
              </Field>
              {errors.counselling_done && touched.counselling_done && (
                <Typography variant="caption" color="error">
                  {errors.counselling_done}
                </Typography>
              )}
            </FormControl>

            {/* OTP Section */}
            <Box sx={{ mt: 3 }}>
            {!values.skipOtpConsent && ( <Typography variant="body2" fontWeight={500} required sx={{ mb: 1 }}>
                OTP has been sent to mobile number {maskNumber(decryptOpenSSLAES(beneficiaryData?.ben_mobile_number))}  <span style={{ color: "red", fontSize: "20px" }}>*</span>
              </Typography>)}

          {/* OTP Input - only show if skip OTP consent is not checked */}
          {!values.skipOtpConsent && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid #dcdcdc",
                  borderRadius: "4px",
                  padding: "10px",
                  ...(errors.otp && touched.otp && {
                    borderColor: "error.main",
                  }),
                }}
              >
                <Field name="otp">
                  {({ field, meta }) => (
                    <TextField
                      {...field}
                      variant="standard"
                      placeholder="------"
                      fullWidth
                      className="pd-input"
                      InputProps={{
                        disableUnderline: true,
                      }}
                      inputProps={{
                        maxLength: 6,
                        style: {
                          letterSpacing: "6px",
                          fontSize: "16px",
                        },
                      }}
                      error={!!(meta.error && meta.touched)}
                    />
                  )}
                </Field>

                <Button
                  variant="outlined"
                  size="small"
                  disabled={resendTimer > 0}
                  onClick={handleResendOTP}
                  sx={{
                    ml: 2,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    minWidth:"100px"
                  }}
                >
                  {resendTimer > 0 ? `Resend (${resendTimer}s)` : "Resend"}
                </Button>
              </Box>

              {errors.otp && touched.otp && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  {errors.otp}
                </Typography>
              )}

                  {/* Timer display */}
                  {resendTimer > 0 && (
                    <Typography sx={{ mt: 1, fontSize: "13px", color: "text.secondary" }}>
                      {resendTimer} sec
                    </Typography>
                  )}
                </>
              )}

              {/* Skip OTP Link */}
              {!showSkipOtpConsent && !values.skipOtpConsent && (
              <Typography sx={{ mt: 1, fontSize: "13px" }}>
                Do you want to{" "}
                <span 
                  style={{ color: "#3f51b5", cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => handleSkipOtpClick(setFieldValue)}
                >
                  skip OTP?
                </span>
              </Typography>
              )}

              {/* Skip OTP Consent Checkbox */}
              {showSkipOtpConsent && (
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Field name="skipOtpConsent">
                        {({ field }) => (
                          <Checkbox
                            {...field}
                            checked={values.skipOtpConsent}
                            onChange={(e) => {
                              setFieldValue("skipOtpConsent", e.target.checked);
                              // Reset authentication fields when unchecking
                              if (!e.target.checked) {
                                setFieldValue("verification_method", "");
                                setFieldValue("verification_value", "");
                                setShowSkipOtpConsent(false);
                              }
                            }}
                            color="primary"
                          />
                        )}
                      </Field>
                    }
                    label={
                      <Typography sx={{ fontSize: "14px" }}>
                        I hereby confirm that the beneficiary did not receive the One Time Password (OTP)
                      </Typography>
                    }
                  />
                </Box>
              )}

              {/* Authentication Type Selection - only show if skip OTP consent is checked */}
              {values.skipOtpConsent && (
                <>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight={500} required sx={{ mb: 1 }}>
                      Select Authentication Type
                    </Typography>
                    <FormControl 
                      fullWidth 
                      error={!!(errors.verification_method && touched.verification_method)}
                    >
                      <Field name="verification_method">
                        {({ field, meta }) => (
                          <Select
                            {...field}
                            value={values.verification_method}
                            onChange={(e) => {
                              setFieldValue("verification_method", e.target.value);
                              // Reset verification value when type changes
                              setFieldValue("verification_value", "");
                            }}
                            displayEmpty
                            sx={{ mt: 1 }}
                          >
                            <MenuItem value="" disabled>
                              Select Authentication Type
                            </MenuItem>
                            {authenticationTypes.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </Field>
                      {errors.verification_method && touched.verification_method && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {errors.verification_method}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>

                  {/* Authentication Value Input */}
                  {values.verification_method && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {values.verification_method === "multifactorial_password" 
                          ? "Enter Multifactorial Password" 
                          : authenticationTypes.find(type => type.value === values.verification_method)?.label || "Authentication Value"}
                      </Typography>
                      
                      {/* ADD THIS HELPER TEXT FOR MULTIFACTORIAL PASSWORD */}
                      {values.verification_method === "multifactorial_password" && (
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                          Enter the first four letters of beneficiary name (case insensitive) followed by date of birth in DDMM format.
                        </Typography>
                      )}
    
                    <Field name="verification_value">
                      {({ field, meta }) => (
                        <TextField
                          {...field}
                          fullWidth
                          placeholder={
                            values.verification_method === "year_of_birth" 
                              ? "Year of Birth" 
                              : values.verification_method === "multifactorial_password"
                              ? "MUKE1201 (Mukesh born on 12th jan)"
                              : "Enter value"
                          }
                          type={values.verification_method === "year_of_birth" ? "number" : "text"}
                          inputProps={{
                            ...(values.verification_method === "year_of_birth" && {
                              maxLength: 4,
                              min: 1900,
                              max: new Date().getFullYear(),
                            }),
                            ...(values.verification_method === "beneficary_reference_id" && {
                              maxLength: 4,
                            }),
                            ...(values.verification_method === "multifactorial_password" && {
                              maxLength: 8,
                              style: { textTransform: "uppercase" }, // Auto-uppercase for consistency
                            }),
                          }}
                          className="pd-input"
                          error={!!(meta.error && meta.touched)}
                          helperText={meta.error && meta.touched ? meta.error : ""}
                          sx={{ mt: 1 }}
                        />
                      )}
                    </Field>
                  </Box>
                )}
                </>
              )}
            </Box>

            {/* Verify Button */}
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSubmitting || formSubmitting}
              sx={{
                mt: 4,
                borderRadius: 2,
                py: 1.5,
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {isSubmitting && <CircularProgress size={20} color="inherit" />}
              Verify & Continue
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default PersonalDetailsuwin;